import React, { Component } from 'react';
import {
    Alert,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Label,
    Nav,
    NavItem,
    NavLink,
    Row,
    TabContent,
    TabPane
} from 'reactstrap';

import BreadCrumb from '../../Components/Common/BreadCrumb';
import { StatisticsAPI } from "api";
import { Loading } from "custom";

import PivotTableUI from 'react-pivottable/PivotTableUI';
import { aggregators as defaultAggregators } from 'react-pivottable/Utilities';
import TableRenderers from 'react-pivottable/TableRenderers';
import 'react-pivottable/pivottable.css';
import './statistics.css';

const moment = require("moment");

const CURRENT_YEAR = moment().year();
const FIRST_YEAR = 2023;


// ============================================================
// Aggregatori in italiano (riusano le funzioni built-in)
// ============================================================
const itAggregators = {
    "Conteggio": defaultAggregators["Count"],
    "Conteggio valori unici": defaultAggregators["Count Unique Values"],
    "Lista valori unici": defaultAggregators["List Unique Values"],
    "Somma": defaultAggregators["Sum"],
    "Somma intera": defaultAggregators["Integer Sum"],
    "Media": defaultAggregators["Average"],
    "Mediana": defaultAggregators["Median"],
    "Varianza campionaria": defaultAggregators["Sample Variance"],
    "Deviazione standard": defaultAggregators["Sample Standard Deviation"],
    "Minimo": defaultAggregators["Minimum"],
    "Massimo": defaultAggregators["Maximum"],
    "Primo valore": defaultAggregators["First"],
    "Ultimo valore": defaultAggregators["Last"],
    "Somma su somma totale": defaultAggregators["Sum over Sum"],
    "Conteggio % righe": defaultAggregators["Count as Fraction of Rows"],
    "Conteggio % colonne": defaultAggregators["Count as Fraction of Columns"],
    "Conteggio % totale": defaultAggregators["Count as Fraction of Total"],
    "Somma % righe": defaultAggregators["Sum as Fraction of Rows"],
    "Somma % colonne": defaultAggregators["Sum as Fraction of Columns"],
    "Somma % totale": defaultAggregators["Sum as Fraction of Total"],
};

// ============================================================
// Renderer in italiano (chiavi mappate ai nomi reali esportati da TableRenderers)
// ============================================================
const itRenderers = {
    "Tabella": TableRenderers["Table"],
    "Mappa di calore": TableRenderers["Table Heatmap"],
    "Mappa di calore (per riga)": TableRenderers["Table Row Heatmap"],
    "Mappa di calore (per colonna)": TableRenderers["Table Col Heatmap"],
    "Esporta TSV": TableRenderers["Exportable TSV"],
};


// ============================================================
// Etichette campi in italiano (chiave = campo backend, valore = label visibile)
// ============================================================
const FIELD_LABELS = {
    // tempo (comuni)
    year: "Anno",
    month: "Mese",
    month_num: "N° mese",
    day: "Giorno",
    week: "Settimana",
    weekday: "Giorno della settimana",
    date: "Data",
    created_at: "Data creazione",

    // leads
    lead_id: "ID lead",
    consulent_id: "ID consulente",
    consulent_name: "Consulente",
    module_id: "ID modulo",
    module_name: "Modulo",
    referral_platform: "Piattaforma",
    country: "Paese",
    status: "Stato",
    trial_done: "Lezione di prova fatta",
    appointment_done: "Appuntamento preso",
    appointment_confirmed: "Appuntamento confermato",
    converted: "Convertito",
    has_report: "Da segnalazione",
    reporter_name: "Segnalatore",

    // sells
    sell_id: "ID vendita",
    client_id: "ID cliente",
    client_name: "Cliente",
    client_country: "Paese cliente",
    type: "Tipo",
    level_macro: "Livello",
    level_name: "Livello specifico",
    course_name: "Corso video",
    total: "Totale",
    is_free: "Omaggio",
    reconfirm: "Riconferma",
    group_sell: "Gruppo vendita",

    // payments
    payment_id: "ID pagamento",
    sell_type: "Tipo vendita",
    sell_level_macro: "Livello vendita",
    sell_level_name: "Livello vendita specifico",
    sell_total: "Totale vendita",
    sell_reconfirm: "Riconferma vendita",
    value: "Importo lordo",
    commission: "Commissione",
    transaction_fee: "Costo transazione",
    net_value: "Importo netto",
    type_payment: "Metodo pagamento",
    verified: "Verificato"
};


// Trasforma i dati rinominando le chiavi in italiano
function relabelDataset(dataset) {
    if (!Array.isArray(dataset) || dataset.length === 0) return dataset;
    return dataset.map((row) => {
        const out = {};
        Object.keys(row).forEach((k) => {
            const newKey = FIELD_LABELS[k] || k;
            out[newKey] = row[k];
        });
        return out;
    });
}


// ============================================================
// Tabs e default pivot states (usano le label italiane)
// ============================================================
const TABS = [
    {
        id: "leads",
        label: "Lead",
        apiFn: "leads",
        storageKey: "pivot_state_leads_v2",
        defaultState: () => ({
            rows: ["Consulente"],
            cols: ["Mese"],
            aggregatorName: "Conteggio",
            rendererName: "Tabella"
        })
    },
    {
        id: "sells",
        label: "Vendite",
        apiFn: "sells",
        storageKey: "pivot_state_sells_v2",
        defaultState: () => ({
            rows: ["Consulente"],
            cols: ["Mese"],
            vals: ["Totale"],
            aggregatorName: "Somma",
            rendererName: "Tabella"
        })
    },
    {
        id: "payments",
        label: "Fatturato",
        apiFn: "payments",
        storageKey: "pivot_state_payments_v2",
        defaultState: () => ({
            rows: ["Livello vendita"],
            cols: ["Mese"],
            vals: ["Importo netto"],
            aggregatorName: "Somma",
            rendererName: "Tabella"
        })
    }
];


class StatisticsList extends Component {

    constructor(props) {
        super(props);
        this.state = {
            year: CURRENT_YEAR,
            activeTab: "leads",

            // Cache datasets per (tab, year)
            datasets: {},
            loadings: {},
            errors: {},

            // Stato pivot per tab (persisted)
            pivotStates: this.loadPivotStates()
        };
        document.title = "Statistiche | CLTV Cloud";
    }

    componentDidMount() {
        this.loadDataForActiveTab();
    }

    loadPivotStates = () => {
        const states = {};
        TABS.forEach((t) => {
            try {
                const raw = localStorage.getItem(t.storageKey);
                if (raw) states[t.id] = JSON.parse(raw);
                else states[t.id] = t.defaultState();
            } catch (e) {
                states[t.id] = t.defaultState();
            }
        });
        return states;
    };

    cacheKey = (tabId, year) => tabId + "_" + year;

    loadDataForActiveTab = (forceReload = false) => {
        const { activeTab, year } = this.state;
        const key = this.cacheKey(activeTab, year);

        // Se forzato (pulsante "Aggiorna"/"Riprova"), invalidiamo la cache cosi' la fetch riparte.
        if (forceReload && this.state.datasets[key]) {
            this.setState((prev) => {
                const datasets = { ...prev.datasets };
                delete datasets[key];
                return { datasets };
            }, () => this.loadDataForActiveTab());
            return;
        }

        if (this.state.datasets[key]) return;

        const tab = TABS.find(t => t.id === activeTab);
        if (!tab) return;

        this.setState((prev) => ({
            loadings: { ...prev.loadings, [key]: true },
            errors: { ...prev.errors, [key]: false }
        }));

        StatisticsAPI[tab.apiFn](year).then((data) => {
            const relabeled = relabelDataset(data);
            this.setState((prev) => ({
                datasets: { ...prev.datasets, [key]: relabeled },
                loadings: { ...prev.loadings, [key]: false }
            }));
        }).catch((err) => {
            console.error(err);
            this.setState((prev) => ({
                loadings: { ...prev.loadings, [key]: false },
                errors: { ...prev.errors, [key]: true }
            }));
        });
    };

    // Reset dell'errore + re-fetch per il tab indicato (usato dal pulsante "Riprova").
    retryActiveTab = (tabId) => {
        const key = this.cacheKey(tabId, this.state.year);
        this.setState((prev) => ({
            errors: { ...prev.errors, [key]: false }
        }), () => this.loadDataForActiveTab(true));
    };

    changeYear = (e) => {
        const newYear = Number(e.target.value);
        this.setState({ year: newYear }, () => this.loadDataForActiveTab());
    };

    changeTab = (tabId) => {
        if (tabId === this.state.activeTab) return;
        this.setState({ activeTab: tabId }, () => this.loadDataForActiveTab());
    };

    onPivotStateChange = (tabId, newState) => {
        const cleanState = { ...newState };
        delete cleanState.aggregators;
        delete cleanState.renderers;
        delete cleanState.tableOptions;

        this.setState((prev) => ({
            pivotStates: { ...prev.pivotStates, [tabId]: newState }
        }));

        try {
            const tab = TABS.find(t => t.id === tabId);
            if (tab) localStorage.setItem(tab.storageKey, JSON.stringify(cleanState));
        } catch (e) {
            // ignore quota errors
        }
    };

    resetPivotState = (tabId) => {
        const tab = TABS.find(t => t.id === tabId);
        if (!tab) return;
        const def = tab.defaultState();
        this.setState((prev) => ({
            pivotStates: { ...prev.pivotStates, [tabId]: def }
        }));
        try {
            localStorage.removeItem(tab.storageKey);
        } catch (e) {
            // ignore
        }
    };

    renderYearSelect() {
        const years = [];
        for (let y = CURRENT_YEAR; y >= FIRST_YEAR; y--) years.push(y);

        return (
            <div className="d-flex align-items-center gap-2">
                <Label className="mb-0 me-2"><b>Anno</b></Label>
                <Input
                    type="select"
                    value={this.state.year}
                    onChange={this.changeYear}
                    style={{ maxWidth: 140 }}
                >
                    {years.map((y) => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </Input>
                {(this.state.year === CURRENT_YEAR) ? (
                    <small className="text-muted ms-2">
                        (dati dal 1 gennaio al {moment().format("DD/MM/YYYY")})
                    </small>
                ) : null}
            </div>
        );
    }

    renderTabContent(tab) {
        const key = this.cacheKey(tab.id, this.state.year);
        const data = this.state.datasets[key];
        const loading = this.state.loadings[key];
        const error = this.state.errors[key];

        if (loading) return <div className="p-5"><Loading /></div>;
        if (error) return (
            <Alert color="danger" className="d-flex justify-content-between align-items-center">
                <span>Errore nel caricamento dei dati.</span>
                <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => this.retryActiveTab(tab.id)}
                >
                    <i className="ri-refresh-line align-bottom me-1"></i>
                    Riprova
                </button>
            </Alert>
        );
        if (!data) return <div className="p-5 text-center text-muted">In caricamento...</div>;
        if (data.length === 0) return <Alert color="info">Nessun dato disponibile per l'anno selezionato.</Alert>;

        const pivotState = this.state.pivotStates[tab.id] || {};

        return (
            <div>
                <div className="mb-3 d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                        <b>{data.length}</b> righe nel dataset
                    </small>
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => this.retryActiveTab(tab.id)}
                        >
                            <i className="ri-download-cloud-2-line align-bottom me-1"></i>
                            Aggiorna dati
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => this.resetPivotState(tab.id)}
                        >
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            Reset configurazione
                        </button>
                    </div>
                </div>
                <div className="pivot-it-wrapper" style={{ overflow: 'auto' }}>
                    <PivotTableUI
                        data={data}
                        onChange={(s) => this.onPivotStateChange(tab.id, s)}
                        aggregators={itAggregators}
                        renderers={itRenderers}
                        {...pivotState}
                    />
                </div>
            </div>
        );
    }

    render() {
        return (
            <React.Fragment>
                <div className="page-content">
                    <Container fluid>
                        <BreadCrumb title="Statistiche" pageTitle="CLTV" />

                        <Card>
                            <CardBody>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        {this.renderYearSelect()}
                                    </Col>
                                </Row>

                                <Nav tabs className="nav-tabs-custom mb-3">
                                    {TABS.map((tab) => (
                                        <NavItem key={tab.id}>
                                            <NavLink
                                                className={(this.state.activeTab === tab.id) ? "active fw-semibold cursor-pointer" : "cursor-pointer"}
                                                onClick={() => this.changeTab(tab.id)}
                                            >
                                                {tab.label}
                                            </NavLink>
                                        </NavItem>
                                    ))}
                                </Nav>

                                <TabContent activeTab={this.state.activeTab}>
                                    {TABS.map((tab) => (
                                        <TabPane tabId={tab.id} key={tab.id}>
                                            {(this.state.activeTab === tab.id) ? this.renderTabContent(tab) : null}
                                        </TabPane>
                                    ))}
                                </TabContent>
                            </CardBody>
                        </Card>
                    </Container>
                </div>
            </React.Fragment>
        );
    }
}

export default StatisticsList;
